import React , { useCallback } from 'react';
import {
  Show, SimpleShowLayout, required, Toolbar, SaveButton, BooleanInput, NumberField,
  DateField, TabbedForm, FormTab, Edit, Create, Filter, List, Datagrid, TextField,
  TextInput, BooleanField, ReferenceArrayField, SingleFieldList, ChipField, useRefresh,
  useNotify, ReferenceInput, AutocompleteInput, downloadCSV
} from 'react-admin';
import { useForm } from 'react-final-form';
import styles from './styles/filtersLayout.module.css';
import { AQRPage, BlockedAttributesField } from './components/aqr_page';
import { Box } from '@material-ui/core';
import {
  SiteFilter, TopLevelDomainFilter, PublisherFilter, CountryFilter, DeviceTypeFilter,
  ImpressionTypeFilter
} from './common/filters/Filters';
import DeleteWithConfirmation from './components/DeleteWithConfirmation';
import BulkExportButton from './components/BulkExportButton';

const blockedCreativeAttributes = require('./components/blockedCreativeAttributes.json');
const IABCategoryChoices = require('./components/iab_categories.json');
const iabCategoryObj = IABCategoryChoices.reduce((a,b) => ({...a, [b.id]: b.name}), {});

const nonAdminFilter = (record) => record && record.created_role && record.created_role !== "admin" ? {created_role: record.created_role, created_id: record.created_id} : {}

const AdQualityRuleTitle = ({ record }) => {
  return <span>Ad Quality Rule {record ? `"${record.name}"` : ''} {record && record.active ? 'ACTIVE' : 'INACTIVE'}</span>;
};

const AdQualityRuleFilter = ({ permissions, ...props }) => (
  <Filter {...props}>
    <TextInput label="Search" source="search_name" alwaysOn resettable />
    <BooleanInput source="active" label="Active" style={{ color: 'white' }} alwaysOn resettable />
  </Filter>
);

const exporter = (aqrs) => {
  const aqrsForExport = aqrs.map(aqr => (
      {
        'ID': aqr.code,
        'Name': `"${aqr.name}"`,
        'Blocked Creative Attributes': aqr.battr && aqr.battr.length > 0 ? aqr.battr.map(x=> `"${blockedCreativeAttributes[x]}"`) : undefined,
        'Blocked Creative IDs': aqr.bcrid ? aqr.bcrid.split('\n').map(x=>`"${x}"`) : undefined,
        'Blocked Advertiser Domains': aqr.badv ? aqr.badv.split('\n').map(x=>`"${x}"`) : undefined,
        'Blocked Advertiser Categories': aqr.bcat && aqr.bcat.length > 0 ? aqr.bcat.map(x=> `"${iabCategoryObj[x]}"`) : undefined
      }
    )
  )
  const csv = aqrsForExport.reduce((a,b) => 
      a.concat( [Object.entries(b).reduce((p,n) => 
        n[1] ? p.concat(n[0],n[1],'') : p,[])]),[]).reduce((a,b)=> 
          a.map((e,i) => e + ',' + (b[i]? b[i] : ''))
    ).join('\n');

  downloadCSV(csv, 'ad_quality_rules');
};

const BulkActionButtons = ({resource, selectedIds}) => {
  return (
    <>
      <BulkExportButton
        resource={resource}
        selectedIds={selectedIds}
        exporter={exporter}
      />
    </>
  )
};

const SettingsStringField = (props) => {
  const record = props.record;
  const field = props.field;
  const label = props.label;
  if (!props.record[field]) return <div />;
  const arr = record[field].split('\n');
  return (
    <div>
      <br />
      <label className="MuiFormLabel-root MuiInputLabel-root RaLabeled-label-392 MuiInputLabel-shrink" data-shrink="true">
        <span>{label}</span>
      </label>
      <span className="MuiTypography-root MuiTypography-body2">
        {arr.length > 5 ? (arr.splice(0, 5).join(' ') + ' ...') : arr.join(' ')}
      </span>
    </div>);
};

const SettingsListField = (props) => {
  const record = props.record;
  const field = props.field;
  const label = props.label;
  if (!props.record[field]) return <div />;
  return (
    <div>
      <br />
      <label className="MuiFormLabel-root MuiInputLabel-root RaLabeled-label-392 MuiInputLabel-shrink" data-shrink="true">
        <span>{label}</span>
      </label>
      {record[field].map((item, index) => {
        if (index < 5) {
          return (
            <li key={item}>{item}</li>
          );
        }
      }
      )}
    </div>
  );
};

const FilterListField = (props) => {
  const record = props.record;
  const field = props.field;
  const label = props.label;
  if (record.segment && record.segment[field] && record.segment[field].values && record.segment[field].values.length > 0) {
    return (
    <div>
      <br />
      <label className="MuiFormLabel-root MuiInputLabel-root RaLabeled-label-392 MuiInputLabel-shrink" data-shrink="true">
        <span>{label} ({record.segment[field].exclude ? 'Exclude' : 'Include'})</span>
      </label>
        {record.segment[field].values.map(item => (
          <li key={item}>{item}</li>
        ))}
    </div>
    );
  }
  return <div />;
};

const checkFilter = (filter) => {
  if (!filter) return false;
  if (Array.isArray(filter.values) && filter.values.length > 0) { return true; }
  return false;
};

const AdQualityRulePreview = props => {
  return (
    <Show {...props} actions={false}>
      <SimpleShowLayout>
        {checkFilter(props.record.segment.filter_publisher) && <ReferenceArrayField source="segment.filter_publisher.values" label="Publishers Filtered" reference="publishers">
          <SingleFieldList>
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>}
        {checkFilter(props.record.segment.filter_site) &&
          <ReferenceArrayField source="segment.filter_site.values" label="Sites Filtered" reference="sites">
            <SingleFieldList>
              <ChipField source="name" />
            </SingleFieldList>
          </ReferenceArrayField>}
        {<FilterListField field='filter_country' label='Filter Countries' />}
        {<FilterListField field='filter_device_type' label='Filter Devices' />}
        {<FilterListField field='filter_impression_type' label='Filter Impression Type' />}
        {props.record.battr && <BlockedAttributesField />}
        {<SettingsStringField field='bcrid' label='Blocked Creative IDs' />}
        {<SettingsStringField field='badv' label='Blocked Advertisers' />}
        {<SettingsListField field='bcat' label='Blocked Advertiser Categories' />}
        {<SettingsStringField field='blocked_native_text' label='Blocked Native Text' />}
      </SimpleShowLayout>
    </Show>
  );
};

export const AdQualityRuleList = ({ permissions, ...props }) => (
  <List {...props} title="Ad Quality Rules"
    filter={{type: 'AD_QUALITY_RULE'}}
    filters={<AdQualityRuleFilter permissions={ permissions } />}
    perPage={25}
    exporter={false}
    bulkActionButtons={<BulkActionButtons />}>
    <Datagrid className={styles.buttonRight} rowClick="edit" expand={<AdQualityRulePreview />}>
      {permissions && permissions.role === 'admin' && 
        <TextField source="code" label="ID" />
      }
      <TextField source="name" label="Name" />
      {permissions && permissions.role === 'admin' &&
        <TextField source="created_name" label="Creator" />
      }
      <DateField source="updated_at" label="Update Date" />
      <DateField source="created_at" label="Creation Date" />
      <BooleanField source="active" label="Active" />
      <DeleteWithConfirmation {...props}/>
    </Datagrid>
  </List>
);

const CustomToolbar = props => {
  const notify = useNotify();
  const form = useForm();

  const handleClick = useCallback(() => {
    if (form.getState().valid) {
      form.submit();
      return;
    } else if (form.getState().errors['minAdminFilters']){ 
      notify(form.getState().errors['minAdminFilters'].message, { type: 'error' })
    }
  }, [form]);

  return (
    <Toolbar {...props}>
      <SaveButton onClick={handleClick}/>
      { props.formType === 'edit' && <DeleteWithConfirmation style={{marginLeft: '20px'}} size='medium' /> }
      <br />
      <DateField source="updated_at" label="Update date" style={{ padding: '10px' }} showTime />
      <TextField source="updated_name" label="" style={{ padding: '10px' }} />
      { !form.getState().valid && form.getState().errors['minAdminFilters'] && <p style={{ color: 'red' }}>{form.getState().errors['minAdminFilters'].message}</p> }
    </Toolbar>
  )
};

const AdQualityRuleForm = (props) => {
  const filtersValidation = (vals) => {
    const errors = {};
    const filters = ['publisher', 'site', 'user'];
    if (filters.every(f => (props.permissions && props.permissions.role === 'admin') &&
                           (vals && vals.segment && vals.segment[`filter_${f}`]) &&
                           (!vals.segment[`filter_${f}`].values ||
                            vals.segment[`filter_${f}`].values.length === 0)
                          )
                        ) {
      errors['minAdminFilters'] = {message: 'At least a Publisher or Site filter must be selected'}
      return errors;
    }
  }

  return (
    <TabbedForm validate={filtersValidation} toolbar={<CustomToolbar {...props}/>} {...props}>
      <FormTab label="Settings">
        <BooleanInput source="active" label="Active" defaultValue={true} validate={required()} />
        { props.permissions && props.permissions.role === 'admin' && <TextField source="code" label="Ad Quality Rule ID" /> }
        <TextInput source="name" label="Ad Quality Rule Name" style={{ width: '400px' }} validate={required()} />
        { props.permissions && props.permissions.role === 'admin' && props.formType === 'edit' &&
          <ReferenceInput source="created_id" label="AQR Owner"
            reference='users'
            basePath='users'
            sort={{ field: 'name', order: 'ASC' }}
            filterToQuery={searchText => ({ search_name: searchText })}
            emptyText="- None -">
            <AutocompleteInput label="Users" optionText={e => e? `${e.name} - ${e.role}` : ''} optionValue="code" fullWidth />
          </ReferenceInput>
        }
        <TextInput source="type" defaultValue="AD_QUALITY_RULE" style={{ visibility: 'hidden', display: 'none' }} />
      </FormTab>
      <FormTab label="Ad Quality Rules">
        <AQRPage permissions={props.permissions} props/>
      </FormTab>
      <FormTab label="Filters">
        <Box className={styles.dualGrid} fullWidth>
          <Box className={styles.flexColumn} fullWidth>
            <PublisherFilter filter={nonAdminFilter(props.record)} {...props}/> 
            <SiteFilter filter={nonAdminFilter(props.record)} {...props}/>
            <TopLevelDomainFilter {...props}/>
          </Box>

          <Box className={styles.flexColumn} fullWidth>
            <CountryFilter {...props} />
            <DeviceTypeFilter {...props} />
            <ImpressionTypeFilter {...props} />
          </Box>
        </Box>
      </FormTab>
    </TabbedForm>
  )
};

export const AdQualityRuleEdit = (props) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const onSuccessFn = ({data}) => {
    notify(`Changes to Ad Quality Rule were saved`);
    refresh();
  }
  return (
  <Edit title={<AdQualityRuleTitle />} mutationMode="pessimistic" onSuccess={onSuccessFn} {...props}>
    <AdQualityRuleForm formType="edit" {...props}/>
  </Edit>
  )
};

export const AdQualityRuleCreate = (props) => (
  <Create title="Create AdQualityRule" mutationMode="pessimistic" {...props}>
    <AdQualityRuleForm formType="create" {...props}/>
  </Create>
);
